import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from '../../../models/app';
import { ActionButtonProps, AppActions } from './types';
import { useInstallAppMutation } from '../../../redux/api/appMarket-api-slice';
import LoadingButton from '../../Common/LoadingButton';
import { isInstallAppButtonDisabled } from '../../../component-helpers/app-actions';
import { AppInstallResponse } from '../../../models/post-req-responses-payloads';
import { usePermission } from '../../../hooks/usePermission';
import { AppMarketPermissions } from '../../../models/enums';
import { useMerchantDelinquencyStatus } from '../../../hooks/useMerchantDelinquencyStatus';
import { useCollectionsRelatedToApp } from '../../../hooks/useCollectionsRelatedToApp';
import { useAppInstallRequiresAnotherPlan } from '../../../hooks/useAppInstallRequiresAnotherPlan';
import {useQueryForInstallApp} from '../../../hooks/useQueryForInstallApp';

interface ComponentProps extends ActionButtonProps<AppInstallResponse> {
  app: App;
  appSubscriptionId: Uuid;
}

export const InstallAppButton: React.FC<ComponentProps> = (
  {
    app,
    appSubscriptionId,
    actionCallBack,
    text,
  }): Nullable<JSX.Element> => {
  const { id: appId } = app;
  const { t } = useTranslation();
  const [installApp] = useInstallAppMutation();
  const [processing, setProcessing] = useState<boolean>(false);
  const { enabled: hasInstallUninstallPermission } = usePermission(AppMarketPermissions.InstallUninstallApps);
  const isMerchantDelinquent = useMerchantDelinquencyStatus();
  const collectionsRelatedToApp = useCollectionsRelatedToApp(appId); // needed to invalidate collection cache (only the collections associated to the app)
  const { requiresAnotherPlan } = useAppInstallRequiresAnotherPlan(app);
  const buttonText = text || t(AppActions.Install);
  useLayoutEffect(() => {
    setProcessing(false); // let the loader spin until new action comes in
  }, [app]);

  const urlQuery = useQueryForInstallApp(appSubscriptionId);
  const connectApp = async () => {
    setProcessing(true);
    const reqResponse = await installApp({
      body: urlQuery,
      appId,
      collectionsRelatedToApp,
    });
    actionCallBack?.({
      reqResponse,
      app
    });
    setTimeout(() => {
      setProcessing(false);
    }, 3000);
  };

  return (
    <LoadingButton
      text={buttonText}
      isLoading={processing}
      onClick={connectApp}
      isDisabled={processing || isInstallAppButtonDisabled(app, hasInstallUninstallPermission, isMerchantDelinquent, requiresAnotherPlan)}
    />
  );
};
